/**
 * Creates a generator function to process promises with specified concurrency.
 *
 * @param nodes The array of items to be processed.
 * @param task The asynchronous task to be performed on each item.
 * @param concurrent The maximum number of concurrent promises.
 * @returns A generator function for processing promises.
 */
export async function* makePromiseIterator<T, R>(
  nodes: T[],
  task: (arg: T) => Promise<R>,
  concurrent = 4,
  timeout = 5000
): AsyncGenerator<Awaited<R> | Error, void, undefined> {
  const queue = nodes.map(task);

  while (queue.length) {
    const batch = queue.splice(0, concurrent);
    const results = await Promise.all(
      batch.map(p => Promise.race([p, timeoutPromise(timeout)]))
    );
    for (const result of results) {
      yield result;
    }
  }
}

const timeoutPromise = (ms: number): Promise<Error> =>
  new Promise(resolve => setTimeout(() => resolve(new Error('Operation timed out')), ms));

/**
 * Processes an array of items with a specified asynchronous task and concurrency.
 *
 * @param args The array of items to be processed.
 * @param task The asynchronous task to be performed on each item.
 * @param concurrent The maximum number of concurrent promises.
 * @returns A Promise resolving to the results of the asynchronous tasks.
 */
export async function runThroughArguments<T, R>(
  args: T[],
  task: (arg: T) => Promise<R>,
  concurrent = 4,
  onProgress?: (completed: number, total: number) => void
): Promise<(Awaited<R> | Error)[]> {
  if (args.length === 0) return [];

  const results: (Awaited<R> | Error)[] = [];
  let completed = 0;

  for await (const result of makePromiseIterator(args, task, concurrent)) {
    results.push(result);
    completed++;
    onProgress?.(completed, args.length);
  }

  return results;
}
